import { Result } from '@models/campaign';
import { ItemResponse } from '@models/order/items/response';

/**
 * Fase 2 — DESCONTO_ATINGIR_META.
 *
 * Cada linha do resultado escolhe um "tipo de benefício" (enum
 * CampanhaResultadoTipoEnum do backend). Cada regra de cálculo é isolada numa
 * classe própria (BenefitCalculator), selecionada por `getBenefitCalculator`.
 *
 * Convenção de grafia herdada do backend: "PORPORCIONAL".
 */
export const BENEFIT_DESCONTO = 'DESCONTO';
export const BENEFIT_FIXA = 'BONIFICACAO_FIXA';
export const BENEFIT_PORPORCIONAL = 'BONIFICACAO_PORPORCIONAL';
export const BENEFIT_DESCONTO_FIXA = 'DESCONTO_BONIFICACAO_FIXA';
export const BENEFIT_DESCONTO_PORPORCIONAL = 'DESCONTO_BONIFICACAO_PORPORCIONAL';

const BONUS_BENEFITS = new Set<string>([
    BENEFIT_FIXA,
    BENEFIT_PORPORCIONAL,
    BENEFIT_DESCONTO_FIXA,
    BENEFIT_DESCONTO_PORPORCIONAL,
]);

/** Tipos de benefício que geram bonificação (fixa ou proporcional). */
export const isBonusBenefit = (tipo: string | null | undefined): boolean =>
    BONUS_BENEFITS.has(tipo ?? '');

export interface BenefitOutcome {
    /** Percentual de desconto (0-100) aplicado na LINHA DE VENDA do item. */
    desconto: number;
    /** Quantidade a bonificar (grátis), na unidade de `bonusMultiplo`. */
    bonusQty: number;
    /** Múltiplo/apresentação da linha grátis gerada no pedido. */
    bonusMultiplo: number;
}

export interface BenefitCalculator {
    readonly tipo: string;
    calculate(result: Result, item: ItemResponse | undefined): BenefitOutcome;
}

const num = (v: unknown): number => Number(v) || 0;

const emptyOutcome: BenefitOutcome = {
    desconto: 0,
    bonusQty: 0,
    bonusMultiplo: 1,
};

/**
 * Bonificação fixa: quantidade exata de unidades avulsas (apresentação 1),
 * conforme configurado na linha. Independe de quanto ultrapassou a meta.
 */
const fixBonus = (r: Result): { bonusQty: number; bonusMultiplo: number } => ({
    bonusQty: num(r.qtd),
    bonusMultiplo: 1,
});

/**
 * Bonificação proporcional (base: caixas inteiras), sempre a preço sem desconto:
 *
 *   valorBonificacao = qtdPercentual% × precoSemDesconto × qtd × apresentação
 *   precoCaixa = precoSemDesconto × apresentação
 *   qtdBonificada (caixas) = floor(valorBonificacao / precoCaixa)
 *
 * O preço se cancela, então o cálculo é feito direto como
 * floor(qtdPercentual% × qtd). Isso não depende do preço do item, que não vem
 * nos pedidos de venda carregados da API na tela de bonificação. Menos de uma
 * caixa inteira resulta em 0 (sem bonificação).
 */
const proportionalBonus = (
    item: ItemResponse | undefined,
    r: Result,
): { bonusQty: number; bonusMultiplo: number } => {
    const presentation = num(item?.quantityPresentation) || 1;
    const exactBoxes = (num(r.qtdPercentual) / 100) * num(item?.quantity);
    // Arredonda antes do floor para evitar 3,9999… virar 3 por ponto flutuante.
    const boxes = Math.floor(Math.round(exactBoxes * 100) / 100);
    return { bonusQty: Math.max(0, boxes), bonusMultiplo: presentation };
};

class DescontoBenefit implements BenefitCalculator {
    readonly tipo = BENEFIT_DESCONTO;
    calculate(result: Result): BenefitOutcome {
        return { ...emptyOutcome, desconto: num(result.desconto) };
    }
}

class BonificacaoFixaBenefit implements BenefitCalculator {
    readonly tipo = BENEFIT_FIXA;
    calculate(result: Result): BenefitOutcome {
        return { desconto: 0, ...fixBonus(result) };
    }
}

class BonificacaoProporcionalBenefit implements BenefitCalculator {
    readonly tipo = BENEFIT_PORPORCIONAL;
    calculate(result: Result, item: ItemResponse | undefined): BenefitOutcome {
        return { desconto: 0, ...proportionalBonus(item, result) };
    }
}

class DescontoBonificacaoFixaBenefit implements BenefitCalculator {
    readonly tipo = BENEFIT_DESCONTO_FIXA;
    calculate(result: Result): BenefitOutcome {
        return { desconto: num(result.desconto), ...fixBonus(result) };
    }
}

class DescontoBonificacaoPorporcionalBenefit implements BenefitCalculator {
    readonly tipo = BENEFIT_DESCONTO_PORPORCIONAL;
    calculate(result: Result, item: ItemResponse | undefined): BenefitOutcome {
        return {
            desconto: num(result.desconto),
            ...proportionalBonus(item, result),
        };
    }
}

const calculators: Record<string, BenefitCalculator> = {
    [BENEFIT_DESCONTO]: new DescontoBenefit(),
    [BENEFIT_FIXA]: new BonificacaoFixaBenefit(),
    [BENEFIT_PORPORCIONAL]: new BonificacaoProporcionalBenefit(),
    [BENEFIT_DESCONTO_FIXA]: new DescontoBonificacaoFixaBenefit(),
    [BENEFIT_DESCONTO_PORPORCIONAL]: new DescontoBonificacaoPorporcionalBenefit(),
};

/**
 * Retorna o calculador do tipo de benefício. Resultados legados (Fase 1) têm
 * `tipo` nulo e caem no comportamento de DESCONTO (retrocompatível).
 */
export const getBenefitCalculator = (
    tipo: string | null | undefined,
): BenefitCalculator => calculators[tipo ?? ''] ?? calculators[BENEFIT_DESCONTO];
